import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridRenderCellParams,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { GridActionsCellItemLink } from '../../components/FakeLink';
import { ProtectedContent } from '../../components/ProtectedContent';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { usePermissions, useSearchParamsState } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { cleanFeeConfigs, fetchFeeConfigs } from '../../redux/modules/feeConfig/feeConfig.actions';
import { feeConfigSelector } from '../../redux/modules/feeConfig/feeConfig.selectors';
import { locations } from '../../routes/locations';
import { FeeConfig, FeeConfigStatus, PeriodicityTypes } from '../../types/feeConfig';
import { parseJSON } from '../../utils/json';
import * as permissions from '../../utils/permissions';
import { productTypeSelector } from '../../redux/modules/productType/product-type.selectors';
import { fetchProductTypes } from '../../redux/modules/productType/product-type.actions';

const feeConfigStatusToInfo: {
  [prop in FeeConfigStatus]: {
    color: 'error' | 'info' | 'success' | 'warning';
    icon: React.ElementType;
    menuAction: string;
  };
} = {
  [FeeConfigStatus.CREATED]: {
    color: 'success',
    menuAction: 'Change to created',
    icon: CheckCircleIcon,
  },
  [FeeConfigStatus.ACTIVE]: {
    color: 'success',
    menuAction: 'Activate',
    icon: CheckCircleIcon,
  },
  [FeeConfigStatus.INACTIVE]: {
    color: 'warning',
    menuAction: 'Deactivate',
    icon: WarningIcon,
  },
  [FeeConfigStatus.DELETED]: {
    color: 'error',
    menuAction: 'Cancel',
    icon: CheckCircleIcon,
  },
};

export function FeeConfigList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const tableRef = useRef<TableRef>(null);
  const [selectedRow, setSelectedRow] = useState<FeeConfig>();
  const [actionMenu, setActionMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const canRead = usePermissions({ items: [permissions.FeeConfig.read] });
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
    },
    {
      field: 'formula',
      headerName: 'Formula',
      type: 'string',
      width: 100,
    },
    {
      field: 'percentage',
      headerName: 'Percentage',
      type: 'number',
      width: 80,
      renderCell: (params: GridRenderCellParams) => {
        const percentageValue = params.row?.percentage || 0;
        return <>{percentageValue}%</>;
      },
    },
    {
      field: 'minSubscription',
      headerName: 'Min Subscription',
      type: 'number',
      width: 130,
    },
    {
      field: 'vat',
      headerName: 'VAT',
      type: 'number',
      width: 50,
      renderCell: (params: GridRenderCellParams) => {
        const percentageValue = params.row?.vat || 0;
        return <>{percentageValue}%</>;
      },
    },
    {
      field: 'calculationPeriodicity.name',
      headerName: 'Calculation Type',
      type: 'singleSelect',
      valueOptions: Object.values(PeriodicityTypes),
      minWidth: 130,
      valueGetter: (params) => params.row?.calculationPeriodicity?.name,
    },
    {
      field: 'deductionPeriodicity.name',
      headerName: 'Deduction Type',
      type: 'singleSelect',
      valueOptions: Object.values(PeriodicityTypes),
      minWidth: 130,
      valueGetter: (params) => params.row?.deductionPeriodicity?.name,
    },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(FeeConfigStatus),
      minWidth: 120,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = feeConfigStatusToInfo[params.row?.status?.name as FeeConfigStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
    },
  ]);
  const canDoActions = usePermissions({ items: [permissions.FeeConfig.statusUpdate] });
  const { totalFeeConfigs, feeConfigs, fetchStatus } = useSelector(feeConfigSelector);
  const {
    fetchStatus: productTypeFetchStatus,
    productTypes,
    error: productTypeError,
  } = useSelector(productTypeSelector);

  const loading = fetchStatus === fetchStatuses.pending || productTypeFetchStatus === fetchStatuses.pending;
  const availableStatuses = Object.values(FeeConfigStatus).filter(
    (s) => s !== selectedRow?.status?.name,
  ) as FeeConfigStatus[];
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '100');

  const getProductTypeNameById = (id: number): string | undefined =>
    productTypes.find((item) => item.id === id)?.nameEn;

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, data: FeeConfig) => {
    setSelectedRow(data);
    setActionMenu(
      actionMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          }
        : null,
    );
  };

  const handleCloseMenu = () => {
    setActionMenu(null);
  };

  const onAddNew = (): void => {
    navigate(locations.feeConfig(0));
  };

  useEffect(
    () => () => {
      dispatch(fetchProductTypes({ limit: 100, offset: 0 }));
      dispatch(cleanFeeConfigs());
    },
    [],
  );

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canRead) {
          actions.push(
            <GridActionsCellItemLink
              key={`fee-config-list-read-${params.row.id}`}
              icon={<OpenInNewIcon />}
              label="Link"
              href={locations.feeConfig(params.row.id)}
            />,
          );
        }

        if (canDoActions) {
          actions.push(
            <GridActionsCellItem
              key={`employee-list-actions-${params.row.id}`}
              icon={<MoreVertIcon />}
              label="Actions"
              onClick={(e) => handleMenuOpen(e, params.row)}
            />,
          );
        }

        return actions;
      },
    };

    if (canRead || canDoActions) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canRead, canDoActions]);

  useEffect(() => {
    const productTypeColumn = {
      field: 'productTypeId',
      type: 'singleSelect',
      headerName: 'Product Type',
      minWidth: 180,
      valueGetter: (params) => getProductTypeNameById(params.row?.productTypeId),
    };
    const columnsCopy = [...columns];
    columnsCopy.splice(2, 0, productTypeColumn);
    setColumns(columnsCopy);
  }, []);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Fee Configs &nbsp;
          <ProtectedContent items={[]}>
            <Fab color="primary" size="small" aria-label="Create IndexFund" onClick={onAddNew}>
              <AddIcon />
            </Fab>
          </ProtectedContent>
        </Typography>
      </Box>
      <Table
        ref={tableRef}
        fetchItems={fetchFeeConfigs}
        rows={feeConfigs}
        columns={columns}
        loading={loading}
        rowCount={totalFeeConfigs}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
      />
      <Menu
        open={actionMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          actionMenu !== null
            ? {
                top: actionMenu.mouseY,
                left: actionMenu.mouseX,
              }
            : undefined
        }
      />
    </Container>
  );
}
